/**
 * appInsights.ts
 *
 * Azure Application Insights integration.
 * Provides centralized performance monitoring, distributed tracing,
 * exception tracking, and custom event/metric logging visible in
 * the Azure portal.
 *
 * Initialize by calling initAppInsights() in main.tsx before React renders.
 * Set VITE_APPINSIGHTS_CONNECTION_STRING in your .env file.
 */
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { isProduction, isStaging, isDevelopment, EnvironmentConfig } from './environment';

// Re-export SeverityLevel so callers don't need to import from the SDK directly
export { SeverityLevel };

const CONNECTION_STRING = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const APP_VERSION = import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

let appInsights: ApplicationInsights | null = null;

export type AppInsightsStatus = 'disabled' | 'initializing' | 'connected' | 'error';

let connectionStatus: AppInsightsStatus = 'disabled';

/** Returns the current App Insights connection status */
export function getAppInsightsStatus(): AppInsightsStatus {
  return connectionStatus;
}

/**
 * Initialize Azure Application Insights.
 * Must be called in main.tsx before React renders.
 */
export function initAppInsights(): void {
  if (!CONNECTION_STRING) {
    if (isDevelopment()) {
      console.warn('[AppInsights] Connection string not configured (VITE_APPINSIGHTS_CONNECTION_STRING). Azure monitoring disabled.');
    }
    connectionStatus = 'disabled';
    return;
  }

  connectionStatus = 'initializing';

  try {
  appInsights = new ApplicationInsights({
    config: {
      connectionString: CONNECTION_STRING,
      // Automatic SPA page view tracking on route change
      enableAutoRouteTracking: true,
        // ── Distributed tracing ──────────────────────────────────────────────
        // Adds W3C `traceparent` + `Request-Id` headers to outgoing AJAX
        // requests so the backend OpenTelemetry SDK creates a child span tied
        // to the same trace — enabling end-to-end correlation in App Insights.
      enableCorsCorrelation: true,
        // Exclude third-party domains from correlation header injection.
        // Without this, App Insights injects `Request-Id`/`traceparent` into
        // every XHR/fetch — including Mixpanel SDK requests — which causes
        // CORS preflight failures because those APIs don't allow custom headers.
        correlationHeaderExcludedDomains: [
          'api.mixpanel.com',
          '*.mixpanel.com',
          'browser.sentry-cdn.com',
          '*.sentry.io',
          '*.ingest.sentry.io',
          'fonts.googleapis.com',
          'fonts.gstatic.com',
        ],
        // ── AJAX tracking ────────────────────────────────────────────────────
      enableAjaxPerfTracking: true,
      enableAjaxErrorStatusText: true,
      // Don't track response headers (could contain PII/secrets)
      enableResponseHeaderTracking: false,
      enableRequestHeaderTracking: false,
      autoTrackPageVisitTime: true,
      // ── Exception tracking ───────────────────────────────────────────────
      // Explicitly enable auto exception tracking (default is true, but
      // being explicit prevents accidental disabling in future config changes).
      disableExceptionTracking: false,
      // ── Sampling ────────────────────────────────────────────────────────
      // Do NOT apply client-side sampling — let Azure Application Insights
      // adaptive sampling handle it server-side, which is smarter (it preserves
      // exceptions and rare events). Client-side 20% sampling was randomly
      // dropping error telemetry that we always want to capture.
      samplingPercentage: 100,
      // Disable cookie usage if privacy regulations require it
      disableCookiesUsage: false,
    },
  });

  appInsights.loadAppInsights();

  // Tag all telemetry with environment and version
  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.data) {
      envelope.data['environment'] = ENVIRONMENT;
      envelope.data['appVersion'] = APP_VERSION;
    }
    // Exceptions are always high-value signals — force 100% sampling regardless
    // of any future samplingPercentage config. This ensures no JS error is silently dropped.
    if (envelope.baseType === 'ExceptionData') {
      envelope.sampleRate = 100;
    }
    return true;
  });

    // Send a startup ping — confirm in Azure: traces | where message == "App initialized"
    appInsights.trackTrace({
      message: 'App initialized',
      severityLevel: SeverityLevel.Information,
    }, {
      environment: ENVIRONMENT,
      appVersion: APP_VERSION,
      startupTime: new Date().toISOString(),
    });

    // Flush immediately so the startup ping reaches Azure without waiting for the buffer
    appInsights.flush();

    connectionStatus = 'connected';

  if (isDevelopment()) {
      console.info('[AppInsights] ✅ Connected — startup ping sent. Confirm in Azure portal:');
      console.info('  Logs → traces | where message == "App initialized"');
    }
  } catch (err) {
    connectionStatus = 'error';
    console.error('[AppInsights] ❌ Failed to initialize:', err);
  }
}

/** Set authenticated user context after login (links telemetry to a user). */
export function setAppInsightsUser(userId: string, accountId?: string): void {
  if (!appInsights) return;
  appInsights.setAuthenticatedUserContext(userId, accountId, true);
}

/** Clear user context on logout. */
export function clearAppInsightsUser(): void {
  if (!appInsights) return;
  appInsights.clearAuthenticatedUserContext();
}

/** Track a custom business event visible in App Insights custom events table. */
export function trackAppEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, { ...properties, ...measurements });
}

/**
 * Track an exception — appears in App Insights failures/exceptions blade.
 * @param error - The Error object
 * @param properties - Key/value string pairs for additional context
 * @param severityLevel - Defaults to Error
 */
export function trackAppException(
  error: Error,
  properties?: Record<string, string>,
  severityLevel?: SeverityLevel
): void {
  if (!appInsights) return;
  appInsights.trackException({
    exception: error,
    severityLevel: severityLevel ?? SeverityLevel.Error,
    properties,
  });
}

/** Track a numeric metric (e.g. response time, queue depth). */
export function trackAppMetric(
  name: string,
  value: number,
  properties?: Record<string, string>
): void {
  if (!appInsights) return;
  appInsights.trackMetric({ name, average: value }, properties);
}

/**
 * Write a trace log visible in App Insights traces table.
 * Use for info/warn/debug messages you want searchable in Azure portal.
 */
export function trackAppTrace(
  message: string,
  severityLevel: SeverityLevel = SeverityLevel.Information,
  properties?: Record<string, string>
): void {
  if (!appInsights) return;
  appInsights.trackTrace({ message, severityLevel }, properties);
}

/** Flush buffered telemetry immediately. Call before logout or page unload. */
export function flushAppInsights(): void {
  if (!appInsights) return;
  appInsights.flush();
}

/** Returns true if App Insights has been successfully initialized. */
export function isAppInsightsEnabled(): boolean {
  return appInsights !== null;
}
